import {
    DOC_TYPE_REQUIRED,
    DOC_TYPE_INTERNAL,
    DOC_TYPE_DOWNLOADABLE,
    formalitiesDocumentSelector,
    FormaliteDocument,
} from '@europrocurement/l2d-domain';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    ADD_DOS_DOC_CTX,
    type DocTypeItem,
    DocumentTypeForm,
    useDossierDocumentFilesApi,
    useDossierDocumentsCtxDispatch,
    useDossierRoutingContext,
    useDossierValidationHeader,
    useSelectedDossierContext,
} from '@europrocurement/l2d-modules';
import { Box, Button, Typography, useTheme } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useModal } from '@europrocurement/flexy-components';
import { useSelector } from 'react-redux';

type DocumentTabFooterActionsProps = {
    docType: typeof DOC_TYPE_REQUIRED | typeof DOC_TYPE_INTERNAL | typeof DOC_TYPE_DOWNLOADABLE;
    addDocBtnLabel: string;
    additionalActions?: Array<ReactNode>;
};

const DocumentTabFooterActions: React.FunctionComponent<DocumentTabFooterActionsProps> = function ({
    docType,
    addDocBtnLabel,
    additionalActions = [],
}) {
    const { getDossierValidationRoute } = useDossierRoutingContext();
    const { setHeaderTitle, setHeaderButton, setPathOnExit } = useDossierValidationHeader();
    const navigate = useNavigate();
    const location = useLocation();
    const { palette } = useTheme();
    const { postDosDoc } = useDossierDocumentFilesApi();
    const dossierDocumentsDispatch = useDossierDocumentsCtxDispatch();
    const { dossier } = useSelectedDossierContext();
    const { modalActions } = useModal();
    const dataSource = useSelector(formalitiesDocumentSelector);
    const [labelButton, setLabelButton] = useState<string>('Consulter les documents');

    const handleAddDocument = async (selectedItems: DocTypeItem[]) => {
        // Get DocumentsType structure of selected ids
        const items = dataSource.data.filter((item: FormaliteDocument) =>
            selectedItems.some((selectedItem) => selectedItem.id === (item.id as number)),
        );

        // Link document type to the dossier
        items.map(async (item) => {
            const newDosDoc = await postDosDoc(
                dossier.id as number,
                item.id as number,
                item.libelle as string,
                dossier.statutId as number,
            );

            // Maj document in context
            dossierDocumentsDispatch({
                type: ADD_DOS_DOC_CTX,
                payload: newDosDoc,
            });
        });

        modalActions.close();
    };
    // Display modal of document types to add
    const handleOpenAddDocumentModal = () => {
        modalActions.call(
            <DocumentTypeForm
                title="Ajout de type de document"
                itemsSelected={[]}
                type={docType}
                handleSelectItems={handleAddDocument}
            />,
        );
    };
    useEffect(() => {
        switch (docType) {
            case DOC_TYPE_REQUIRED:
                setLabelButton('Consulter les documents requis');
                break;
            case DOC_TYPE_INTERNAL:
                setLabelButton('Consulter les documents internes');
                break;
            case DOC_TYPE_DOWNLOADABLE:
                setLabelButton('Consulter les documents remis');
                break;

            default:
                setLabelButton('Consulter les documents');
                break;
        }
    }, [docType]);

    const handleNavigateToValidation = () => {
        const path = getDossierValidationRoute(dossier?.id as number);
        const currentLocation = location.pathname + location.search;
        switch (docType) {
            case DOC_TYPE_REQUIRED:
                setHeaderTitle(`${dossier.numero} | Consultation des documents requis`);
                setHeaderButton('Enregister et quitter');
                setPathOnExit(currentLocation);
                break;
            case DOC_TYPE_INTERNAL:
                setHeaderTitle(`${dossier.numero} | Consultation des documents internes`);
                setHeaderButton('Retour aux documents');
                setPathOnExit(currentLocation);
                break;
            case DOC_TYPE_DOWNLOADABLE:
                setHeaderTitle(`${dossier.numero} | Consultation des documents remis`);
                setHeaderButton('Retour aux documents');
                setPathOnExit(currentLocation);
                break;

            default:
                setHeaderTitle(`Vérifier les informations du dossier ${dossier.numero}`);
                setHeaderButton('Enregister et quitter');
                setPathOnExit(currentLocation);
                break;
        }
        if (path) {
            let additionnalQueryParam = '';
            if (docType === DOC_TYPE_REQUIRED) {
                additionnalQueryParam = `?docType=${docType}&layout=documents-1-3`;
            } else {
                additionnalQueryParam = `?docType=${docType}&layout=documents-full`;
            }
            // Navigate to the validation route with display list of documents sent as a query parameter
            navigate(`${path}${additionnalQueryParam}`);
        }
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
                onClick={() => handleOpenAddDocumentModal()}
                sx={{
                    fontWeight: 500,
                    padding: '8px 20px',
                    color: palette.primary.dark,
                }}
            >
                <FaOptionIcon
                    icon={['fas', 'plus']}
                    sx={{ lineHeight: 1, color: 'inherit' }}
                />
                <Typography variant="l2dbodys">{addDocBtnLabel}</Typography>
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', columnGap: '1em' }}>
                {additionalActions}
                <Button
                    variant="contained"
                    onClick={handleNavigateToValidation}
                    sx={{
                        fontWeight: 500,
                        padding: '8px 20px',
                        backgroundColor: palette.primary.dark,
                        ':hover': {
                            backgroundColor: palette.primary.main,
                        },
                    }}
                    key="validate-btn"
                >
                    {labelButton || 'Vérifier les informations'}
                </Button>
            </Box>
        </Box>
    );
};

export default DocumentTabFooterActions;
